import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SERVICES, SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { useSectionViews, type TrackedSection } from "@/hooks/useSectionViews";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

import { regionalHreflang } from "@/lib/hreflang";
import { RegionalLocationSwitcher } from "@/components/site/RegionalLocationSwitcher";

const PATH = "/fractional-cto-us";
const CTA_SOURCE_HERO = "fractional_cto_us_hero";
const CTA_SOURCE_BAND = "fractional_cto_us_footer";

/** Sections reported as read, in page order, keyed by their heading id. */
const TRACKED_SECTIONS: TrackedSection[] = [
  { id: "fcto-hero-heading", name: "Hero" },
  { id: "fcto-definition-heading", name: "What a fractional CTO is" },
  { id: "fcto-when-heading", name: "When founders bring me in" },
  { id: "fcto-services-heading", name: "What I do" },
  { id: "fcto-engagement-heading", name: "How the engagement works" },
  { id: "fcto-compare-heading", name: "Comparison" },
  { id: "fcto-us-heading", name: "Working with US companies" },
  { id: "fcto-quotes-heading", name: "Testimonials" },
  { id: "fcto-faq-heading", name: "FAQ" },
];

const title = "Hire a Fractional CTO | US Fractional CTO Services";
const description =
  "Fractional CTO services for US startups and SMEs. Hire senior technology leadership part-time: audits, delivery assurance, diligence. Timothy Ng, Scryla.";

const FAQ = [
  {
    q: "What is a fractional CTO?",
    a: "A fractional CTO is an experienced chief technology officer engaged part-time rather than as a full-time executive hire. For a US startup that usually means a contracted engagement with no equity grant, no benefits load and no severance exposure, sized to the decisions the company actually faces this quarter.",
  },
  {
    q: "Can a fractional CTO work with a US company from the UK?",
    a: "Yes, and most of my work already runs this way. I engage as a contractor with a US entity, which keeps the arrangement simple on your side, and my working day covers US East Coast mornings. Where a board meeting, an investor session or an on-site review needs me in the room, travel is costed into the engagement rather than assumed.",
  },
  {
    q: "Can a fractional CTO help with SOC 2 or a security questionnaire?",
    a: "SOC 2 is an AICPA attestation performed by a licensed CPA firm, so I cannot issue the report. What I do is decide what is true of your systems today, what has to change before an auditor or an enterprise buyer looks, and in what order, so the questionnaire stops being the thing that holds up the deal.",
  },
  {
    q: "How does a fractional CTO help with US venture diligence?",
    a: "Technical diligence looks at architecture, key-person risk, third-party and open-source dependencies, security posture, and whether the engineering spend maps to the plan. I go through it before the investor does, tell you what they will find, and put the answers in writing.",
  },
  {
    q: "What does a fractional CTO cost?",
    a: "Scoped per engagement, in sterling, after a first conversation. US founders usually ask because they are comparing against a full-time CTO package at US comp levels; the honest comparison is not an hourly rate but what the arrangement is accountable for. Tell me the decision in front of you and I will tell you what it would take.",
  },
  {
    q: "Fractional CTO or VP Engineering: which do I need?",
    a: "A VP Engineering runs the team, the process and the delivery machine day to day. A fractional CTO sets technical direction and carries the decisions that reach the board and the balance sheet. Hiring one when you needed the other is one of the more expensive mistakes available at US comp levels, and I will tell you which gap I think you have.",
  },
];

const WHEN = [
  {
    title: "Your offshore build team is unsupervised",
    body: "A US company with a development team several time zones away needs someone reading delivery against what was promised. Otherwise the only status report you get is written by the people being reported on.",
  },
  {
    title: "A security questionnaire is blocking a deal",
    body: "Enterprise buyers send a SOC 2 or vendor-risk pack and the answers stall. Someone has to decide what is true today, what needs building, and in which order, before the deal slips a quarter.",
  },
  {
    title: "The next round is going to look at the technology",
    body: "US venture diligence goes beyond the deck: architecture, key-person risk, third-party dependencies, what the burn is actually buying. Better to know what they will find before they find it.",
  },
  {
    title: "Your first senior engineering hire is the wrong shape",
    body: "Founders reach for a VP Engineering when the gap is technical strategy, or a CTO title when the need is delivery management. Getting the shape wrong is expensive in US comp and equity.",
  },
];

const COMPARE = [
  {
    title: "Fractional CTO",
    body: "Executive-level judgement at a cadence that matches the stage, with no salary, equity grant or severance attached. Independent of any build shop, so nothing is being sold to you underneath the advice.",
  },
  {
    title: "Full-time CTO",
    body: "The right call once the engineering org needs daily leadership. Before that point, US executive comp and equity make it an expensive bet to place on an unproven fit, and a slow one to unwind.",
  },
  {
    title: "Development agency or dev shop",
    body: "Strong at building the thing you specified. Structurally poor at telling you the specification is wrong, because the revenue depends on the build continuing.",
  },
];

export const Route = createFileRoute("/fractional-cto-us")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta(
        "fractional-cto-us",
        "Fractional CTO for US startups, remote-first | Timothy Ng",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }, ...regionalHreflang],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO for US companies | Timothy Ng, Scryla",
          areaServed: AREAS.us,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.us,
        }),
      ),
      breadcrumbMeta(PATH),
    ],
  }),
  component: FractionalCtoPage,
});

function FractionalCtoPage() {
  useSectionViews(PATH, TRACKED_SECTIONS);

  return (
    <div>
      <section
        aria-labelledby="fcto-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Breadcrumbs path={PATH} />
            <RegionalLocationSwitcher region="us" />
          </div>
          <Eyebrow>Fractional CTO</Eyebrow>
          <h1
            id="fcto-hero-heading"
            className="mt-4 max-w-[20ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Hire a fractional CTO for your US startup.
          </h1>
          <DirectAnswer className="mt-6">
            A fractional CTO is an experienced chief technology officer engaged part-time instead of
            as a full-time executive. For US companies that usually means senior technical judgement
            on architecture, delivery, vendors and spend on a set cadence, with overlap arranged
            around US working hours and travel costed per engagement.
          </DirectAnswer>
          <Lead className="mt-5">
            Deadlines slipping, burn rising, and nobody senior accountable for the technology?
            Former CTO and COO, and founder of my own consultancy since 2014. I work with founders
            who need senior technical leadership on the decisions that matter, without committing to
            a full-time executive hire.
          </Lead>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fcto-definition-heading">
        <Container>
          <h2
            id="fcto-definition-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What a fractional CTO is
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              A fractional CTO is an experienced chief technology officer engaged part-time. You get
              the judgement of someone who has run technology at executive level, applied to the
              decisions that carry real cost, on a contract you can size up or wind down as the
              company changes.
            </p>
            <p>
              For a US company that typically covers architecture and delivery calls, offshore or
              agency oversight, security and compliance readiness ahead of enterprise buyers and
              auditors, engineering spend against the plan, and being the person your board or your
              investors can put a technical question to and get a straight answer.
            </p>
            <p>
              I take a small number of clients at a time. I have no build team to keep busy and no
              vendor referral fees to protect, so nothing in the advice is pointing anywhere except
              at what I think you should do.
            </p>
          </div>
          <RelatedLinks className="mt-7" paths={["/what-i-do"]} />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fcto-when-heading">
        <Container>
          <Eyebrow>When to hire one</Eyebrow>
          <h2
            id="fcto-when-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The moments founders call me
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fcto-services-heading">
        <Container>
          <h2
            id="fcto-services-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What you get
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {SERVICES.map((service) => (
              <li key={service.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{service.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{service.short}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section
        className="border-b border-border bg-surface"
        aria-labelledby="fcto-engagement-heading"
      >
        <Container>
          <h2
            id="fcto-engagement-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            How the engagement works
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              I contract with the US entity directly, which keeps the arrangement simple: no
              employment, no benefits load, no equity conversation, and a scope you can review at
              any point. Cadence follows the stage, a one-off audit with a written report, a fixed
              involvement through a delivery programme, or standing availability as the technical
              voice you call before a decision is signed.
            </p>
            <p>
              Everything lands in writing. Boards, investors and engineering teams read the same
              document, which removes most of the arguments about what was actually said.
            </p>
            <p>
              No lock-in, and no interest in staying longer than the business needs me. When the
              company is ready to bring the role in-house, I help you hire for it and step back.
            </p>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fcto-compare-heading">
        <Container>
          <Eyebrow>Choosing between the options</Eyebrow>
          <h2
            id="fcto-compare-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Fractional CTO, full-time CTO or an agency
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {COMPARE.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fcto-us-heading">
        <Container>
          <h2
            id="fcto-us-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Working with US companies
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body">
            <p>
              I have advised US companies remotely, working to their hours rather than asking them
              to take calls at the end of my day. I am based in {SITE.location}, so my working day
              overlaps with US East Coast mornings, which is when most of the conversations that
              matter happen anyway.
            </p>
            <p>
              Remote is how the work usually runs: written reports, scheduled calls and availability
              between them. Where being in the room changes the outcome, I travel, and the travel is
              costed into the engagement rather than assumed. Sectors I know well include fintech,
              iGaming and gambling, digital health, SaaS, data and agencies.
            </p>
          </div>
          <div className="mt-8 max-w-[62ch] rounded-lg border border-border bg-card p-6">
            <h3 className="text-lg font-semibold">
              North America: an iGaming programme review in Canada
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-body">
              I flew out to Canada to review a stalled iGaming programme. The technology plan and
              the commercial plan had drifted apart, and the pace each side assumed was different.
              The work was strategic realignment: put every option on the table, including the ones
              nobody wanted to say out loud, and agree one direction the business and the
              engineering team could both hold. It took millions out of the projected cost and
              shortened the route to delivery.
            </p>
          </div>
          <RelatedLinks
            className="mt-7"
            paths={["/fractional-cto-cost", "/fractional-cto-vs-full-time-vs-agency"]}
          />
          <RelatedLinks
            className="mt-8"
            label="Other regions"
            variant="cards"
            paths={["/fractional-cto-uk", "/fractional-cto-southeast-asia"]}
          />
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fcto-quotes-heading">
        <Container>
          <h2 id="fcto-quotes-heading" className="sr-only">
            What founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.milanShah} />
            <Quote item={TESTIMONIALS.danielReilly} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fcto-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fcto-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Fractional CTO FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks className="mt-7" paths={["/technology-non-executive-director-uk"]} />
        </Container>
      </section>

      <CtaBand
        heading="Need a fractional CTO?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
